"use client"

import { Button } from "@/components/ui/button"
import { STATUS_BADGE_TONE_CLASS } from "@/lib/list-status-badges"
import {
  authoringConfidenceAfter,
  authoringConfidenceScore,
  confidenceDelta,
  qualitySuggestionLines,
  questionQualityFieldSiblings,
  selectHomeRecommendations,
  type QualitySuggestionLine,
  type QuestionQualityAlternate,
  type QuestionQualityCheck,
} from "@/lib/question-quality"
import { cn } from "@/lib/utils"

export const REVIEW_WASH =
  "bg-[linear-gradient(180deg,color-mix(in_oklch,var(--exxat-color-danger,var(--destructive))_8%,transparent),color-mix(in_oklch,var(--exxat-color-danger,var(--destructive))_3%,transparent))]"
export const CAUTION_WASH =
  "bg-[linear-gradient(180deg,color-mix(in_oklch,var(--exxat-color-warning,var(--chart-4))_10%,transparent),color-mix(in_oklch,var(--exxat-color-warning,var(--chart-4))_3%,transparent))]"

export function ConfidenceMeter({
  value,
  compareTo,
  now,
}: {
  value: number
  compareTo?: number
  now?: boolean
}) {
  const safe = Math.max(0, Math.min(100, value))
  const delta = compareTo == null ? "same" : confidenceDelta(safe, compareTo)
  const label = now
    ? `Authoring confidence now ${safe}%`
    : delta === "up"
      ? `Authoring confidence ${safe}% after this, up from ${compareTo}%`
      : delta === "down"
        ? `Authoring confidence ${safe}% after this, down from ${compareTo}%`
        : `Authoring confidence ${safe}% after this`
  const valueClass =
    now || delta === "same"
      ? "border border-border bg-surface-2 text-foreground"
      : delta === "up"
        ? STATUS_BADGE_TONE_CLASS.success
        : STATUS_BADGE_TONE_CLASS.danger
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium tabular-nums",
        valueClass,
      )}
      aria-label={label}
    >
      <i className="fa-light fa-gauge text-[0.7rem]" aria-hidden="true" />
      {safe}%
      {delta === "up" ? (
        <i className="fa-light fa-arrow-up text-[0.65rem]" aria-hidden="true" />
      ) : null}
      {delta === "down" ? (
        <i className="fa-light fa-arrow-down text-[0.65rem]" aria-hidden="true" />
      ) : null}
    </span>
  )
}

function optionMark(id: string | undefined): string | null {
  if (!id) return null
  const letter = id.trim()
  if (letter.length === 1 && /[a-z]/i.test(letter)) return letter.toUpperCase()
  return null
}

function QualitySuggestionPreview({ lines }: { lines: QualitySuggestionLine[] }) {
  return (
    <ul className="m-0 min-w-0 flex-1 list-none space-y-2 p-0">
      {lines.map((line) => {
        const mark = optionMark(line.optionId)
        const note = line.replaces
          ? `Replaces ${line.replaces}`
          : line.isNew
            ? "New option"
            : line.isKept
              ? "Correct option"
              : undefined
        const label = note ? `${line.text}. ${note}` : line.text
        return (
          <li
            key={`${line.optionId ?? "new"}:${line.text}`}
            className="list-none"
          >
            <p
              className="flex items-start gap-2 text-sm leading-snug"
              aria-label={label}
            >
              {mark ? (
                <span
                  className="mt-0.5 w-4 shrink-0 text-center text-[0.7rem] font-medium text-muted-foreground"
                  aria-hidden="true"
                >
                  {mark}
                </span>
              ) : null}
              <span className="min-w-0 flex-1">
                <span className="block font-medium text-foreground">
                  {line.text}
                </span>
                {note ? (
                  <span className="mt-0.5 block text-[0.7rem] font-normal text-muted-foreground">
                    {note}
                  </span>
                ) : null}
              </span>
            </p>
          </li>
        )
      })}
    </ul>
  )
}

export function QuestionQualityFinding({
  check,
  siblingChecks,
  surface = "hover",
  bleedTop = true,
  quiet = false,
}: {
  check: QuestionQualityCheck
  siblingChecks?: QuestionQualityCheck[]
  surface?: "hover" | "panel"
  bleedTop?: boolean
  quiet?: boolean
}) {
  const review = check.severity === "review"
  const openChecks =
    siblingChecks && siblingChecks.length > 0 ? siblingChecks : [check]
  const nowScore = authoringConfidenceScore(openChecks)

  if (quiet) {
    return (
      <section aria-label={check.title} className="space-y-1">
        <p className="text-sm font-medium leading-snug text-foreground">
          {check.title}
        </p>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {check.mechanism}
        </p>
      </section>
    )
  }

  return (
    <section
      aria-label={check.title}
      className={cn(
        "space-y-2",
        surface === "hover" && bleedTop
          ? "-mx-4 -mt-4 px-4 pb-3 pt-4"
          : "rounded-2 px-3 py-3",
        review ? REVIEW_WASH : CAUTION_WASH,
      )}
    >
      <div className="space-y-1">
        <div className="flex items-start justify-between gap-3">
          <p className="text-base font-medium leading-snug">{check.title}</p>
          <ConfidenceMeter value={nowScore} now />
        </div>
        <p className="text-[0.7rem] font-normal leading-snug text-muted-foreground">
          {review ? "Blocks Ready" : "Does not block Ready"}
        </p>
      </div>
      <p className="text-sm leading-relaxed text-muted-foreground">
        {check.mechanism}
      </p>
    </section>
  )
}

export function QuestionQualitySuggestedEnhancements({
  check,
  siblingChecks,
  onUse,
  disabled,
  recs,
}: {
  check: QuestionQualityCheck
  siblingChecks?: QuestionQualityCheck[]
  onUse: (secondary?: boolean, alternate?: QuestionQualityAlternate) => void
  disabled?: boolean
  recs?: QuestionQualityAlternate[]
}) {
  const openChecks =
    siblingChecks && siblingChecks.length > 0 ? siblingChecks : [check]
  const textRecs =
    recs ??
    selectHomeRecommendations(
      questionQualityFieldSiblings(openChecks, check),
      openChecks,
    )
  const nowScore = authoringConfidenceScore(openChecks)
  const afterById = new Map(
    textRecs.map((alternate) => [
      alternate.id,
      authoringConfidenceAfter(openChecks, alternate),
    ]),
  )
  const singleApply = textRecs.length === 1

  if (textRecs.length === 0) return null

  return (
    <section className="space-y-2" aria-label="Suggested enhancements">
      <p className="text-xs font-medium text-muted-foreground">
        Suggested enhancements
      </p>
      <div
        className={cn(
          "grid gap-2",
          textRecs.length > 1 && "@min-[32rem]/qq-hover:grid-cols-2",
        )}
      >
        {textRecs.map((alternate) => {
          const after = afterById.get(alternate.id) ?? nowScore
          const optionLines = qualitySuggestionLines(check, alternate)
          return (
            <div
              key={alternate.id}
              className="space-y-2 rounded-2 border border-border bg-surface-2 p-3"
            >
              <div className="flex items-start justify-between gap-3">
                {optionLines.length > 0 ? (
                  <QualitySuggestionPreview lines={optionLines} />
                ) : (
                  <p className="min-w-0 flex-1 text-sm font-medium leading-snug">
                    {alternate.text}
                  </p>
                )}
                <ConfidenceMeter value={after} compareTo={nowScore} />
              </div>
              <Button
                type="button"
                size="sm"
                variant={singleApply ? "default" : "outline"}
                disabled={disabled}
                onClick={() =>
                  onUse(
                    alternate.actionKind === check.secondaryActionKind,
                    alternate,
                  )
                }
              >
                {alternate.actionLabel}
              </Button>
            </div>
          )
        })}
      </div>
    </section>
  )
}
